import { Coordinates } from '@wemap/geo';
import { Itinerary, ItineraryInfoManager } from '@wemap/routers';
import type { Map } from 'maplibre-gl';
import type { WemapMap } from '@wemap/map';
import { EMPTY_FEATURE_COLLECTION, setGeoJsonSourceData } from './geoJsonSource.js';

import InstructionManager from './nav-instruction-manager.js';
import InstructionManagerV1 from './nav-instruction-manager-v1.js';

const LAYER_NAME = 'userposition';
const SOURCE_NAME = 'userposition';

export default class UserPositionHandler {
    /** @type {ItineraryInfoManager} */
    _itineraryInfoManager;

    /** @type {Map} */
    _map: Map;

    _geojson: any;

    /** @type {Coordinates} */
    _userPosition: any;

    _currentFloorLevel = null;

    /** @type {Itinerary} */
    _itinerary: any;

    static LAYER_NAME = LAYER_NAME;
    static SOURCE_NAME = SOURCE_NAME;

    _canvas: any;
    _unsubscribeLevelChange: (() => void) | null = null;

    constructor(wemapMap: WemapMap) {

        this._itineraryInfoManager = new ItineraryInfoManager();

        /** @type {Map} */
        const map = this._map = wemapMap.maplibre;
        const canvas = this._canvas = map.getCanvasContainer();

        wemapMap.addSource(SOURCE_NAME, {
            type: 'geojson',
            data: EMPTY_FEATURE_COLLECTION,
        });

        wemapMap.addLayer({
            id: LAYER_NAME,
            type: 'circle',
            source: SOURCE_NAME,
            paint: {
                'circle-color': '#0000ff',
                'circle-radius': 10,
                'circle-stroke-width': 1
            }
        });


        map.on('mouseenter', LAYER_NAME, () => {
            map.setPaintProperty(LAYER_NAME, 'circle-color', '#3bb2d0');
            canvas.style.cursor = 'move';
        });

        map.on('mouseleave', LAYER_NAME, () => {
            map.setPaintProperty(LAYER_NAME, 'circle-color', '#3887be');
            canvas.style.cursor = '';
        });

        map.on('mousedown', LAYER_NAME, (e: any) => {
            e.preventDefault();

            canvas.style.cursor = 'grab';

            map.on('mousemove', this._onMove);
            map.once('mouseup', this._onUp);
        });

        map.on('touchstart', LAYER_NAME, (e: any) => {
            if (e.points.length !== 1) {
                return;
            }

            e.preventDefault();
            map.on('touchmove', this._onMove);
            map.once('touchend', this._onUp);
        });

        this._unsubscribeLevelChange = wemapMap.onLevelChange((level) => {
            if (this._userPosition && level !== null) {
                this._userPosition.level = level;
                this._updateInstruction();
            }
        });
    }

    _onMove = (e: any) => {
        this._canvas.style.cursor = 'grabbing';

        const coords = new Coordinates(e.lngLat.lat, e.lngLat.lng, null, this._userPosition?.level);
        if (coords.equals(this._userPosition)) {
            return;
        }

        this._userPosition = coords;

        this._geojson.geometry.coordinates = [coords.lng, coords.lat];
        setGeoJsonSourceData(this._map, SOURCE_NAME, this._geojson);

        this._updateInstruction();
    };

    _onUp = () => {
        this._canvas.style.cursor = '';
        this._map.off('mousemove', this._onMove);
        this._map.off('touchmove', this._onMove);
    };

    async init(pos: any) {

        this._userPosition = new Coordinates(pos.lat, pos.lng, null, pos.level ?? null);
        this._updateInstruction();

        this._geojson = {
            type: 'Feature',
            geometry: {
                type: 'Point',
                coordinates: [pos.lng, pos.lat]
            }
        };
        setGeoJsonSourceData(this._map, SOURCE_NAME, this._geojson);
    }

    /** @param {Itinerary} itinerary */
    set itinerary(itinerary: any) {
        let itineraryB = null;
        if (itinerary) {
            // Workaround to success instanceof
            itineraryB = Itinerary.fromJson(itinerary.toJson());
            // Workaround to make itinerary with null level working
            itineraryB.forceUnknownLevelTo0();
        }
        this._itineraryInfoManager.itinerary = itineraryB;
        this._updateInstruction();
    }

    onStepInstructionChange(proposedInstruction: any, currentInstruction: any): void {}

    _updateInstruction() {
        if (!this._userPosition || !this._itineraryInfoManager.itinerary) {
            return;
        }

        // Workaround to make itinerary with null level working
        const modifiedPosition = this._userPosition.clone();
        modifiedPosition.level ||= 0;

        const proposedInstruction = InstructionManager.getInstructionFromPosition(
            this._itineraryInfoManager, modifiedPosition);
        const currentInstruction = InstructionManagerV1.getInstructionFromPosition(
            this._itineraryInfoManager, modifiedPosition);

        if (this.onStepInstructionChange) {
            this.onStepInstructionChange(proposedInstruction || '', currentInstruction || '');
        }

        if (proposedInstruction === null) {
            console.error(
                'Itinerary info is null for :',
                modifiedPosition,
                this._itineraryInfoManager.itinerary
            );
        }
    }
}
